import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ChevronLeft, ChevronRight, Zap, Flame, Sparkles, BadgePercent } from "lucide-react";
import api from "../../lib/api";
import MPlayShell, { VCard, SCard, Avatar, fmtViews, timeAgo } from "./VidShared";
import { Loader2 } from "lucide-react";
import { AdSlot, AdNetworkScripts } from "../../ads/AdEngine";

function HeroCarousel({ items }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), 5500);
    return () => clearInterval(t);
  }, [items.length]);
  if (!items.length) return null;
  const v = items[i];
  return (
    <div className="relative rounded-3xl overflow-hidden mp-fade" data-testid="mplay-hero">
      <div className="relative h-[240px] sm:h-[300px] lg:h-[320px]">
        <AnimatePresence mode="wait">
          <motion.div key={v.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="absolute inset-0">
            {v.thumbnail_url
              ? <img src={v.thumbnail_url} alt={v.title} className="h-full w-full object-cover" />
              : <div className="h-full w-full" style={{ background: "linear-gradient(120deg,#0f2a1a,#123a5f)" }} />}
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(4,10,7,.92) 5%, rgba(4,10,7,.45) 55%, rgba(4,10,7,.15))" }} />
          </motion.div>
        </AnimatePresence>
        <div className="mp-kw absolute inset-0 flex flex-col justify-center px-6 sm:px-10 max-w-[60%]">
          <motion.h1 key={`t${v.id}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            className="font-extrabold text-white text-2xl sm:text-4xl lg:text-[42px] leading-[1.08] tracking-tight" data-testid="mplay-hero-title">
            Explore. Learn.<br />Grow with <span className="text-emerald-400">MPlay</span>
          </motion.h1>
          <p className="text-white/70 text-xs sm:text-sm font-semibold mt-2 sm:mt-3 line-clamp-1">{v.title}</p>
          <Link to={`/v/${v.slug}`} data-testid="mplay-hero-watch"
            className="mp-green inline-flex items-center gap-2 h-10 sm:h-11 px-6 rounded-full text-[13px] sm:text-sm font-extrabold w-fit mt-4 sm:mt-5 transition-colors">
            <Play className="h-4 w-4 fill-current" /> Watch Now
          </Link>
        </div>
        {items.length > 1 && (
          <>
            <button onClick={() => setI((i - 1 + items.length) % items.length)} data-testid="mplay-hero-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => setI((i + 1) % items.length)} data-testid="mplay-hero-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"><ChevronRight className="h-5 w-5" /></button>
            <div className="absolute bottom-3.5 left-1/2 -translate-x-1/2 flex gap-1.5">
              {items.map((x, xi) => <button key={x.id} onClick={() => setI(xi)} className={`h-1.5 rounded-full transition-all ${xi === i ? "w-5 bg-emerald-400" : "w-1.5 bg-white/40"}`} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PromoRail() {
  return (
    <div className="hidden xl:flex flex-col gap-5 w-[300px] shrink-0" data-testid="mplay-promos">
      <Link to="/creator/join" className="mp-kw relative rounded-3xl overflow-hidden h-[200px] group block" data-testid="mplay-promo-create">
        <div className="absolute inset-0" style={{ background: "linear-gradient(140deg,#14532d,#052e16)" }} />
        <div className="relative p-5 flex flex-col h-full">
          <p className="font-extrabold text-white text-2xl leading-tight">Create<br />Share<br />Be Seen</p>
          <p className="text-white/60 text-xs mt-2 font-semibold">Join MPlay Creators — Free</p>
          <span className="mp-green mt-auto w-fit px-5 h-9 rounded-full text-xs font-extrabold inline-flex items-center group-hover:brightness-110">Start Creating</span>
        </div>
      </Link>
      <Link to="/videos?category=Business" className="mp-kw relative rounded-3xl overflow-hidden h-[200px] group block" data-testid="mplay-promo-business">
        <div className="absolute inset-0" style={{ background: "linear-gradient(140deg,#1e3a5f,#0b1a2e)" }} />
        <div className="relative p-5 flex flex-col h-full">
          <p className="font-extrabold text-white text-2xl leading-tight">Business<br />Growth<br />Tips</p>
          <p className="text-white/60 text-xs mt-2 font-semibold">Learn from top creators</p>
          <span className="mp-green mt-auto w-fit px-5 h-9 rounded-full text-xs font-extrabold inline-flex items-center group-hover:brightness-110">Watch Now</span>
        </div>
      </Link>
    </div>
  );
}

const Section = ({ title, icon: Icon, children, testid }) => (
  <section className="mt-8" data-testid={testid}>
    <h2 className="flex items-center gap-2 text-white font-extrabold text-[17px] mb-4">{Icon && <Icon className="h-5 w-5 text-emerald-400" />}{title}</h2>
    {children}
  </section>
);

export default function VideosHome() {
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const pill = params.get("category") || "";
  const [results, setResults] = useState(null);

  useEffect(() => { api.get("/vid/home").then((r) => setData(r.data)).catch(() => setData({})); }, []);
  useEffect(() => {
    if (!q && !pill) { setResults(null); return; }
    api.get(`/vid/search?q=${encodeURIComponent(q)}&category=${encodeURIComponent(pill === "All" ? "" : pill)}`)
      .then((r) => setResults(r.data.videos || [])).catch(() => setResults([]));
  }, [q, pill]);

  const setPill = (p) => { const n = new URLSearchParams(params); p ? n.set("category", p) : n.delete("category"); setParams(n); };
  const heroItems = useMemo(() => (data?.featured?.length ? data.featured : data?.recommended || []).slice(0, 5), [data]);

  return (
    <MPlayShell pills pillActive={pill} onPill={setPill}>
      <AdNetworkScripts slug="mplay" page="home" />
      <div className="px-3 sm:px-6 pt-4 max-w-[1500px] mx-auto">
        {results !== null ? (
          <section data-testid="mplay-search-results">
            <h2 className="text-white font-extrabold text-lg mb-4">
              {q ? `Results for “${q}”` : `${pill} videos`} <span className="text-white/40 text-sm font-semibold">· {results.length}</span>
            </h2>
            {results.length === 0
              ? <p className="text-white/50 text-sm py-16 text-center">No videos found. Try another search.</p>
              : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-8">{results.map((v) => <VCard key={v.id} v={v} />)}</div>}
          </section>
        ) : !data ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
        ) : (
          <>
            <div className="flex gap-5 items-start">
              <div className="flex-1 min-w-0">
                <HeroCarousel items={heroItems} />

                <AdSlot slug="mplay" page="home" slot="top" />

                <Section title="Recommended for you" icon={Sparkles} testid="mplay-recommended">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-8">
                    {(data.recommended || data.latest || []).map((v) => <VCard key={v.id} v={v} />)}
                  </div>
                </Section>

                <AdSlot slug="mplay" page="home" slot="in_content" />

                {data.shorts?.length > 0 && (
                  <Section title="Shorts" icon={Zap} testid="mplay-shorts-row">
                    <div className="flex gap-3 overflow-x-auto mp-scroll pb-2 -mx-1 px-1">
                      {data.shorts.map((v) => <div key={v.id} className="w-[160px] sm:w-[180px] shrink-0"><SCard v={v} /></div>)}
                      <Link to="/videos/shorts" data-testid="mplay-shorts-all"
                        className="w-[160px] sm:w-[180px] shrink-0 mp-thumb aspect-[9/16] flex flex-col items-center justify-center gap-2 text-emerald-400 font-extrabold text-sm hover:bg-[#1a2b22] transition-colors">
                        <Zap className="h-6 w-6" />View all
                      </Link>
                    </div>
                  </Section>
                )}

                {data.trending?.length > 0 && (
                  <Section title="Trending" icon={Flame} testid="mplay-trending">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-8">
                      {data.trending.map((v) => <VCard key={v.id} v={v} />)}
                    </div>
                  </Section>
                )}

                {data.popular?.length > 0 && (
                  <Section title="Popular on MPlay" icon={Play} testid="mplay-popular">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-8">
                      {data.popular.map((v) => <VCard key={v.id} v={v} />)}
                    </div>
                  </Section>
                )}
              </div>
              <PromoRail />
            </div>

            {/* Affiliate strip */}
            <Link to="/creator/offers" data-testid="mplay-affiliate-strip"
              className="mt-10 flex items-center gap-4 mp-panel rounded-2xl p-5 hover:border-emerald-500/40 transition-colors group">
              <span className="h-11 w-11 rounded-xl mp-green flex items-center justify-center shrink-0"><BadgePercent className="h-5 w-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-white font-extrabold text-[15px]">Earn with every video</p>
                <p className="text-white/50 text-xs mt-0.5">Attach affiliate products from MILTROO businesses & earn commissions</p>
              </div>
              <span className="text-emerald-400 text-sm font-extrabold group-hover:translate-x-1 transition-transform">Explore →</span>
            </Link>

            <AdSlot slug="mplay" page="home" slot="footer" />
          </>
        )}
      </div>
    </MPlayShell>
  );
}
